import React, { useState, useEffect } from 'react';

import { SaveOutlined, UserOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import PermissionsDisplay from '@components/shared/permissions/PermissionsDisplay';
import PermissionsEditor from '@components/shared/permissions/PermissionsEditor';
import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';
import PageHeader from '@shared/PageHeader';
import { Avatar, Card, Form, Input, Button, Space, Spin, Alert, message, Select, Row, Col, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { operatorApiFetch } from '@services/operator/apiService';
import { permissionTemplateService } from '@services/operator/permissionTemplateService';

const OperatorsEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { operator: currentOperator } = useOperatorAuth();
  const [assignmentForm] = Form.useForm();
  const [passwordForm]   = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [savingPassword,   setSavingPassword]   = useState(false);
  const [operator, setOperator] = useState(null);
  const [error, setError] = useState(null);
  const [warehouses, setWarehouses] = useState([]);

  const isSelf = operator && operator.id === currentOperator?.id;
  const hasOperatorsWrite = currentOperator?.permissions?.includes('operators:write') ||
                            currentOperator?.permissions?.includes('*:*');

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [opRes, whRes] = await Promise.all([
        operatorApiFetch(`operators/${id}`),
        operatorApiFetch('warehouses'),
      ]);
      const opData = await opRes.json();
      const whData = await whRes.json();

      if (opData.success && opData.data) {
        setOperator(opData.data);
        assignmentForm.setFieldsValue({ warehouse_id: opData.data.warehouse_id });
      } else {
        throw new Error('Failed to fetch operator');
      }
      if (whData.success && whData.data) setWarehouses(whData.data);
    } catch (err) {
      console.error(err);
      setError(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAssignment = async (values) => {
    setSavingAssignment(true);
    try {
      const res = await operatorApiFetch(`operators/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      const data = await res.json();
      if (data.success) {
        message.success(t('common.message.update_success'));
        fetchAll();
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      console.error(err);
      message.error(t('common.error.update_failed'));
    } finally {
      setSavingAssignment(false);
    }
  };

  const handleSavePermissions = async ({ role, permissions }) => {
    try {
      const url = role ? `operators/${id}/set_role` : `operators/${id}/update_permissions`;
      const method = role ? 'POST' : 'PATCH';
      const body = role ? { role } : { permissions };
      const res = await operatorApiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        await fetchAll();
        return Promise.resolve();
      }
      return Promise.reject(new Error(data.message));
    } catch (err) {
      return Promise.reject(err);
    }
  };

  const handleResetPassword = async (values) => {
    setSavingPassword(true);
    try {
      const res = await operatorApiFetch(`operators/${id}/update_password`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      const data = await res.json();
      if (data.success) {
        message.success(t('common.message.password_update_success'));
        passwordForm.resetFields();
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      console.error(err);
      message.error(t('common.error.password_update_failed'));
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '100px 0' }}><Spin size="large" /></div>;
  if (error)   return <Alert message={t('common.error.title')} description={error} type="error" showIcon />;

  const label = operator ? `${operator.name} (${operator.username})` : '';
  const isDisabled = Boolean(operator?.disabled_at);
  const warehouseName = warehouses.find(w => w.id === operator?.warehouse_id)?.name;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      <PageHeader items={['org_management', 'org_management.operators', { label }]} />

      {/* Identity Card */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Avatar size={56} icon={<UserOutlined />} />
          <div style={{ flex: 1 }}>
            <Space size={12} align="center" style={{ marginBottom: 4 }}>
              <Typography.Text strong style={{ fontSize: 18 }}>{operator?.name || '-'}</Typography.Text>
              {isDisabled ? (
                <Tag color="error">{t('operator.operators.edit.account_status.disabled')}</Tag>
              ) : (
                <Tag color="success">{t('operator.operators.edit.account_status.active')}</Tag>
              )}
              {warehouseName && <Typography.Text type="secondary">· {warehouseName}</Typography.Text>}
            </Space>
            <div>
              <Typography.Text type="secondary">
                @{operator?.username}
                {operator?.email ? ` · ${operator.email}` : ''}
              </Typography.Text>
            </div>
            {(operator?.created_by || operator?.updated_by || operator?.disabled_at) && (
              <div style={{ marginTop: 6, fontSize: 12 }}>
                <Space size={12} wrap>
                  {operator?.created_by ? (
                    <Typography.Text type="secondary">
                      {t('common.audit.created_by_inviter', { defaultValue: '由' })} {operator.created_by.name} {t('common.audit.invited', { defaultValue: '邀請' })} · {new Date(operator.created_at).toLocaleDateString()}
                    </Typography.Text>
                  ) : (
                    <Typography.Text type="secondary">
                      {t('common.audit.created_at', { defaultValue: '建立於' })} {new Date(operator.created_at).toLocaleDateString()}
                    </Typography.Text>
                  )}
                  {operator?.updated_by && (
                    <Typography.Text type="secondary">
                      {t('common.audit.last_updated_by', { defaultValue: '最後修改' })} {operator.updated_by.name} · {new Date(operator.updated_at).toLocaleString()}
                    </Typography.Text>
                  )}
                  {operator?.disabled_at && (
                    <Typography.Text type="danger">
                      {t('common.audit.disabled_at', { defaultValue: '已停用' })} · {new Date(operator.disabled_at).toLocaleString()}
                    </Typography.Text>
                  )}
                </Space>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Row: 工作分派 + 重置密碼 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card title={<Space><UserOutlined />{t('operator.operators.edit.assignment.title')}</Space>}>
            <Form form={assignmentForm} layout="vertical" onFinish={handleSaveAssignment}>
              <Form.Item
                label={t('models.warehouse.name')}
                name="warehouse_id"
                rules={[{ required: true, message: t('common.validation.warehouse_required') }]}
              >
                <Select
                  placeholder={t('common.placeholder.please_select')}
                  options={warehouses.map(w => ({ value: w.id, label: w.name }))}
                />
              </Form.Item>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={savingAssignment}>
                {t('common.button.save')}
              </Button>
            </Form>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<Space><LockOutlined />{t('operator.operators.edit.reset_password_card.title')}</Space>}>
            {isSelf ? (
              <Alert
                type="info"
                showIcon
                message={
                  <span>
                    {t('operator.operators.edit.reset_password_self_notice')} <Link to="/profile">{t('operator.profile.title')}</Link>
                  </span>
                }
              />
            ) : (
              <Form form={passwordForm} layout="vertical" onFinish={handleResetPassword}>
                <Form.Item
                  label={t('common.field.new_password')}
                  name="password"
                  rules={[
                    { required: true, message: t('common.validation.password_required') },
                    { min: 6, message: t('common.validation.password_min_length') },
                  ]}
                >
                  <Input.Password />
                </Form.Item>
                <Form.Item
                  label={t('common.field.password_confirmation')}
                  name="password_confirmation"
                  dependencies={['password']}
                  rules={[
                    { required: true, message: t('common.validation.password_confirmation_required') },
                    ({ getFieldValue }) => ({
                      validator(_, v) {
                        return !v || getFieldValue('password') === v
                          ? Promise.resolve()
                          : Promise.reject(new Error(t('common.validation.password_mismatch')));
                      },
                    }),
                  ]}
                >
                  <Input.Password />
                </Form.Item>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={savingPassword}>
                  {t('common.button.update_password')}
                </Button>
              </Form>
            )}
          </Card>
        </Col>
      </Row>

      {/* Permissions — rely on the editor's own Card when editable */}
      {hasOperatorsWrite ? (
        <div style={{ marginBottom: 16 }}>
          <PermissionsEditor
            permissions={operator?.permissions || []}
            onSave={handleSavePermissions}
            templateService={permissionTemplateService}
            role="operator"
            canEdit={!isSelf}
            isEditingSelf={isSelf}
            grantableBy={currentOperator?.permissions || []}
          />
        </div>
      ) : (
        <Card
          title={<Space><SafetyOutlined />{t('operator.operators.edit.permissions_tab')}</Space>}
          style={{ marginBottom: 16 }}
        >
          <PermissionsDisplay permissions={operator?.permissions || []} showTitle={false} role="operator" />
          <Alert
            message={t('operator.operators.edit.permissions_notice_title')}
            description={t('operator.operators.edit.permissions_notice_desc')}
            type="info"
            showIcon
            style={{ marginTop: 24 }}
          />
        </Card>
      )}

    </div>
  );
};

export default OperatorsEdit;
